import { X, Download, FileText, Mail, Phone, MapPin, Briefcase, GraduationCap, Award, Calendar, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface Resume {
  id: string;
  title: string;
  content: any;
  file_url: string | null;
  created_at: string;
  updated_at: string;
}

interface Applicant {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  location: string | null;
  job_title: string | null;
  skills: string[] | null;
  experience_level: string | null;
  bio: string | null;
}

interface ApplicantResumePreviewProps {
  applicationId: string;
  applicantName: string;
  onClose: () => void;
}

export function ApplicantResumePreview({ applicationId, applicantName, onClose }: ApplicantResumePreviewProps) {
  const [resume, setResume] = useState<Resume | null>(null);
  const [applicant, setApplicant] = useState<Applicant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadResumeData();
  }, [applicationId]);

  const loadResumeData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get application with resume and applicant details
      const { data: application, error: appError } = await supabase
        .from('applications')
        .select(`
          id,
          resume_id,
          applicant_id
        `)
        .eq('id', applicationId)
        .single();

      if (appError) throw appError;

      // Get resume data
      if (application.resume_id) {
        const { data: resumeData, error: resumeError } = await supabase
          .from('resumes')
          .select('*')
          .eq('id', application.resume_id)
          .single();

        if (resumeError) throw resumeError;
        setResume(resumeData);
      }

      // Get applicant profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', application.applicant_id)
        .single();

      if (profileError) throw profileError;
      setApplicant(profileData);

    } catch (err: any) {
      console.error('Error loading resume:', err);
      setError(err.message || 'Failed to load resume');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (resume?.file_url) {
      window.open(resume.file_url, '_blank');
    } else {
      // Create a text version for download
      const element = document.createElement('a');
      const text = JSON.stringify(resume?.content, null, 2);
      const file = new Blob([text], { type: 'text/plain' });
      element.href = URL.createObjectURL(file);
      element.download = `${applicantName.replace(/\s+/g, '_')}_resume.txt`;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    }
  };

  const renderResumeContent = () => {
    if (!resume) {
      return (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No resume attached to this application</p>
        </div>
      );
    }

    // If there's a file URL, show iframe preview for PDFs
    if (resume.file_url && resume.file_url.endsWith('.pdf')) {
      return (
        <div className="w-full h-[600px] border-2 border-gray-200 rounded-lg overflow-hidden">
          <iframe
            src={resume.file_url}
            className="w-full h-full"
            title="Resume PDF Preview"
          />
        </div>
      );
    }

    // If there's file URL but not PDF, show download option
    if (resume.file_url) {
      return (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-700 font-semibold mb-2">Resume File Attached</p>
          <p className="text-sm text-gray-600 mb-4">
            {resume.file_url.split('/').pop()}
          </p>
          <button
            onClick={handleDownload}
            className="px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition flex items-center gap-2 mx-auto"
          >
            <ExternalLink className="w-5 h-5" />
            Open File
          </button>
        </div>
      );
    }

    // Structured resume content
    const content = resume.content;

    return (
      <div className="space-y-6">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-2">{applicant?.full_name || applicantName}</h2>
          {applicant?.job_title && (
            <p className="text-xl text-white/90 mb-4">{applicant.job_title}</p>
          )}
          <div className="flex flex-wrap gap-4 text-sm">
            {applicant?.email && (
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <span>{applicant.email}</span>
              </div>
            )}
            {applicant?.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>{applicant.phone}</span>
              </div>
            )}
            {applicant?.location && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{applicant.location}</span>
              </div>
            )}
          </div>
        </div>

        {/* Bio/Summary */}
        {applicant?.bio && (
          <div className="bg-white rounded-lg p-6 border-2 border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              Professional Summary
            </h3>
            <p className="text-gray-700 leading-relaxed">{applicant.bio}</p>
          </div>
        )}

        {/* Skills */}
        {applicant?.skills && applicant.skills.length > 0 && (
          <div className="bg-white rounded-lg p-6 border-2 border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Award className="w-5 h-5 text-orange-600" />
              Skills
            </h3>
            <div className="flex flex-wrap gap-2">
              {applicant.skills.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Experience Level */}
        {applicant?.experience_level && (
          <div className="bg-white rounded-lg p-6 border-2 border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-orange-600" />
              Experience Level
            </h3>
            <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-semibold capitalize inline-block">
              {applicant.experience_level}
            </span>
          </div>
        )}

        {/* Structured Resume Content (if available) */}
        {content?.experience && Array.isArray(content.experience) && content.experience.length > 0 && (
          <div className="bg-white rounded-lg p-6 border-2 border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-orange-600" />
              Work Experience
            </h3>
            <div className="space-y-4">
              {content.experience.map((exp: any, index: number) => (
                <div key={index} className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-bold text-gray-900">{exp.position || exp.title}</h4>
                  <p className="text-gray-700">{exp.company}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                    <Calendar className="w-4 h-4" />
                    {exp.startDate} - {exp.endDate || 'Present'}
                  </p>
                  {exp.description && (
                    <p className="text-gray-700 mt-2">{exp.description}</p>
                  )}
                  {exp.bullets && Array.isArray(exp.bullets) && (
                    <ul className="list-disc list-inside mt-2 space-y-1 text-gray-700">
                      {exp.bullets.map((bullet: string, i: number) => (
                        <li key={i}>{bullet}</li>
                      ))}
                    </ul>
                  )}
                  {exp.achievements && Array.isArray(exp.achievements) && (
                    <ul className="list-disc list-inside mt-2 space-y-1 text-gray-700">
                      {exp.achievements.map((achievement: string, i: number) => (
                        <li key={i}>{achievement}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Education */}
        {content?.education && Array.isArray(content.education) && content.education.length > 0 && (
          <div className="bg-white rounded-lg p-6 border-2 border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-orange-600" />
              Education
            </h3>
            <div className="space-y-4">
              {content.education.map((edu: any, index: number) => (
                <div key={index} className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900">{edu.degree}</h4>
                  <p className="text-gray-700">{edu.school || edu.institution}</p>
                  <p className="text-sm text-gray-600">{edu.graduationDate || edu.year}</p>
                  {edu.gpa && <p className="text-sm text-gray-600">GPA: {edu.gpa}</p>}
                  {edu.field && <p className="text-sm text-gray-600">Major: {edu.field}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Raw text content (fallback) */}
        {content?.text && (
          <div className="bg-white rounded-lg p-6 border-2 border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Resume Content</h3>
            <pre className="whitespace-pre-wrap text-gray-700 font-sans text-sm leading-relaxed">{content.text}</pre>
          </div>
        )}

        {/* Resume metadata */}
        <div className="text-xs text-gray-500 text-center">
          Resume last updated: {new Date(resume.updated_at).toLocaleDateString()}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-white border-b-2 border-gray-200 p-6 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Resume Preview</h2>
            <p className="text-gray-600">{applicantName}</p>
          </div>
          <div className="flex items-center gap-3">
            {resume && (
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Resume</h3>
              <p className="text-gray-600">{error}</p>
            </div>
          ) : (
            renderResumeContent()
          )}
        </div>
      </div>
    </div>
  );
}
